import { useState } from 'react';
import { Typography, Button, Divider, Grid, TextField, CircularProgress, Tooltip } from '@mui/material';
import { useCart } from '../../contexts/CartContext';
import CustomSnackbar from '../elements/CustomSnackbar';
import removeCoupon from '../../hooks/coupon/removeCoupon';
import { CartSummaryProps } from '../../types';
import { KEY_COUPON_LOCATION_ID_ALL, KEY_ORDER_TYPE_DEPOSIT, KEY_ORDER_TYPE_EMPTY, KEY_ORDER_TYPE_FULL } from '../../constants';
import { calculateTotalCouponDiscount } from '../../utils';
import InfoIcon from '@mui/icons-material/Info';

const CartSummary: React.FC<CartSummaryProps> = ({ applyCouponCode, readonly, checkout, showPayInFullText }) => {
  const { depositTotal, tuitionTotal, checkoutProcessingFee, checkoutFeeName, grandTotal, coupon, applyCoupon, orderType, payInFullTotal, fetchCart, cartItems, depositTotalWithDiscounts } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const handleApplyCoupon = async () => {
    if (!showCouponInput) {
      setShowCouponInput(true);
      return;
    }

    setIsApplyingCoupon(true);
    try {
      if (applyCouponCode) {
        const success = await applyCouponCode(couponCode);

        if (success) {
          setIsApplyingCoupon(false);
          setShowCouponInput(false);
        }
      }
    } catch (error) {
      setIsApplyingCoupon(false);
      setSnackbar({ open: true, message: 'Failed to apply coupon.', severity: 'error' });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setIsRemovingCoupon(true);
    try {
      const response = await removeCoupon();
      if (response.success) {
        setIsRemovingCoupon(false);
        applyCoupon({ code: null, value: null, type: null, locationId: null, locationName: null });
        setSnackbar({ open: true, message: 'Coupon removed successfully.', severity: 'success' });
        setShowCouponInput(false);
        setCouponCode('');
        await fetchCart();
      } else {
        setIsRemovingCoupon(false);
        setSnackbar({ open: true, message: 'Failed to remove coupon.', severity: 'error' });
      }
    } catch (error) {
      setIsRemovingCoupon(false);
      setSnackbar({ open: true, message: 'An error occurred while removing the coupon.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const totalDiscount = calculateTotalCouponDiscount(cartItems);

  return (
    <Grid container direction="column" className="cart-summary">
      { checkout && (checkoutProcessingFee != 0) && (
        <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <span>Processing Fee{ checkoutFeeName ? ` (${checkoutFeeName})` : '' }:</span>
          <span style={{ textAlign: 'right' }}>
            <b>${Number(checkoutProcessingFee).toFixed(2)}</b>
          </span>
        </Typography>
      )}
      <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>
          Grand Total:
        </span>
        <span style={{ textAlign: 'right' }}>
          <b>${Number(grandTotal.toFixed(2)) > 0 ? (checkout && orderType === KEY_ORDER_TYPE_FULL ? payInFullTotal : grandTotal).toFixed(2) : '0.00'}</b>
          {
            !checkout && Number(grandTotal.toFixed(2)) != Number(payInFullTotal.toFixed(2))
            &&
            <div className="smaller-text blue">
              <b>${Number(payInFullTotal.toFixed(2)) > 0 ? payInFullTotal.toFixed(2) : '0.00'}</b> if paid in full
            </div>
          }
          {
            showPayInFullText && checkout && orderType !== KEY_ORDER_TYPE_DEPOSIT && Number(grandTotal.toFixed(2)) != Number(payInFullTotal.toFixed(2))
            &&
            <div className="smaller-text blue">
              <b>${Number(payInFullTotal.toFixed(2)) > 0 ? (Number(grandTotal) - Number(payInFullTotal)).toFixed(2) : '0.00'}</b> saved by paying in full
            </div>
          }
        </span>
      </Typography>
      <Divider />
      {(!checkout || (checkout && orderType === KEY_ORDER_TYPE_DEPOSIT)) && (
        <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Deposit Total:</span>
          <span><b>${depositTotalWithDiscounts.toFixed(2)}</b></span>
        </Typography>
      )}
      <Divider />
      {(!checkout || (checkout && orderType === KEY_ORDER_TYPE_DEPOSIT)) && (
        <>
          <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0rem' }}>
            <span>Remaining Tuition:</span>
          <span><b>${(tuitionTotal - depositTotal).toFixed(2)}</b></span>
          </Typography>
          <Divider />
        </>
      )}
      <Typography variant="h6" style={{ display: 'none', justifyContent: 'space-between' }}>
        <span>Coupon Code:</span>
        {coupon?.code ? (
          <>
            {!readonly && (
              <span className={`coupon-applied margin-fix ${isRemovingCoupon ? 'removing' : ''}`}>
                {coupon && coupon.locationId !== KEY_COUPON_LOCATION_ID_ALL && (
                  <Tooltip
                    title={`This coupon only applies to events from the ${coupon.locationName || 'specified'} location.`}
                    arrow
                  >
                    <InfoIcon sx={{ marginLeft: 1, cursor: 'pointer', marginRight: 1 }} />
                  </Tooltip>
                )}
                Coupon Applied <Button color="primary" className="remove-coupon" onClick={handleRemoveCoupon} disabled={isRemovingCoupon}>
                  {isRemovingCoupon ? <CircularProgress size={20} /> : 'X'}
                </Button>
              </span>
            )}
            <span className="coupon-applied">
              <b>-${totalDiscount.toFixed(2)}</b>
            </span>
          </>
        ) : (
          <>
            {showCouponInput && !readonly && (
              <TextField
                variant="outlined"
                label="Enter Coupon Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
            )}
            {!readonly && (
              <Button
                color="primary"
                onClick={handleApplyCoupon}
                disabled={isApplyingCoupon}
                className={`${showCouponInput ? 'apply-coupon' : 'add-coupon'} ${isApplyingCoupon ? 'loading-fix' : ''}`}
              >
                {showCouponInput ? (isApplyingCoupon ? <CircularProgress size={18} /> : 'Apply') : 'Add Coupon Code'}
              </Button>
            )}
          </>
        )}
      </Typography>
      {(checkout && orderType != KEY_ORDER_TYPE_EMPTY && orderType === KEY_ORDER_TYPE_DEPOSIT) && (<>
        <Divider />
        <Typography variant="h6" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Due Today:</span>
          <span><b>${Number(depositTotalWithDiscounts.toFixed(2)) > 0 
            ? Number(depositTotalWithDiscounts + checkoutProcessingFee).toFixed(2)
            : '0.00'}</b></span>
        </Typography>
      </>)}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleCloseSnackbar}
      />
    </Grid>
  );
};


export default CartSummary;
